"use client";

import { useMemo } from "react";
import { type OpenDataClient } from "../features/api/openData";
import { useAgentMarketPairs } from "./useAgentMarketPairs";
import { usePairsDetail } from "./usePairsDetail";

interface UseAgentMarketPairsWithDetailOptions {
  /** 查询参数 */
  params: NonNullable<
    Parameters<OpenDataClient<unknown>["pairs"]["getMarketPairsForAgents"]>[0]
  >;
  /** 是否启用查询 */
  enabled?: boolean;
  /** 缓存时间（毫秒） */
  staleTime?: number;
  /** 技术分析类型 */
  type: "short" | "medium" | "long";
}

/**
 * 获取代理市场交易对数据并附带技术分析详情
 *
 * @param options 查询选项
 * @returns 组合查询结果和状态
 */
export const useAgentMarketPairsWithDetail = (
  options: UseAgentMarketPairsWithDetailOptions,
) => {
  const { params, enabled = true, staleTime, type } = options;

  // 第一步：获取市场交易对基础数据
  const {
    data: marketPairsData,
    isLoading: isLoadingMarketPairs,
    isPending: isPendingMarketPairs,
    isFetching: isFetchingMarketPairs,
    error: marketPairsError,
    refetch: refetchMarketPairs,
  } = useAgentMarketPairs({
    params: {
      ...params,
      "enrich.with_ohlc": type.toUpperCase() as any, // 使用类型断言绕过类型检查
    },
    enabled,
    staleTime,
  });

  // 第二步：根据交易对列表获取技术分析详情
  const {
    data: pairsDetailData,
    isLoading: isLoadingPairsDetail,
    isPending: isPendingPairsDetail,
    isFetching: isFetchingPairsDetail,
    error: pairsDetailError,
    refetch: refetchPairsDetail,
  } = usePairsDetail({
    params: {
      exchange: params.exchange as string,
      type,
      pairs: marketPairsData?.pairs ?? [],
    },
    enabled: enabled && (marketPairsData?.pairs?.length ?? 0) > 0, // 只有当有交易对数据时才启用
    staleTime,
  });

  // 合并两个数据源
  const combinedData = useMemo(() => {
    if (!marketPairsData?.marketData || !pairsDetailData) {
      return marketPairsData?.marketData || [];
    }

    return marketPairsData?.marketData?.map((item) => {
      const pairKey = `${item.base}/${item.quote}`;
      const pairDetail =
        pairsDetailData[pairKey as keyof typeof pairsDetailData];
      return {
        ...item,
        // 添加技术分析数据
        rsi_score: pairDetail?.rsi_score,
        rsi_reason: pairDetail?.rsi_reason,
        ema_score: pairDetail?.ema_score,
        ema_reason: pairDetail?.ema_reason,
        macd_score: pairDetail?.macd_score,
        macd_reason: pairDetail?.macd_reason,
        summary_score: pairDetail?.summary_score,
        shareId: pairDetail?.shareId,
      };
    });
  }, [marketPairsData, pairsDetailData]);

  // 组合状态
  const isLoading = isLoadingMarketPairs || isLoadingPairsDetail;
  const isPending = isPendingMarketPairs || isPendingPairsDetail;
  const isFetching = isFetchingMarketPairs || isFetchingPairsDetail;
  const error = marketPairsError || pairsDetailError;

  // 重新获取数据
  const refetch = async () => {
    await refetchMarketPairs();
    if (marketPairsData?.pairs && marketPairsData.pairs.length > 0) {
      await refetchPairsDetail();
    }
  };

  return {
    data: combinedData,
    isLoading,
    isPending,
    isFetching,
    error,
    refetch,
    // 分别暴露原始数据以便需要时使用
    marketPairsData,
    pairsDetailData,
  };
};
